import { memo, useMemo } from "react";
import { Box, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { DateDisplay } from "../DateDisplay";
import { DescriptionCell } from "./DescriptionCell";
import { EntityCell } from "./EntityCell";
import type { PickerField } from "./EditingPickerModal";
import { AccountCell } from "./AccountCell";
import { AmountCell } from "./AmountCell";
import { CategoriesCell } from "./CategoriesCell";
import { RowActionButtons } from "./RowActionButtons";
import { useRowEdit, type EditSession } from "./editSession";
import { deriveRowView } from "./rowView";
import {
  counterpartyMarker,
  resolveCounterparty,
  type AccountNumberIndex,
} from "../../logic/counterparty";
import type {
  AutoTransactionRuleDto,
  CurrencyFormat,
  TransactionId,
  TransactionListDto,
} from "../../api/types";

export type TransactionRowProps = {
  transaction: TransactionListDto;
  session: EditSession;
  matchingRules: AutoTransactionRuleDto[] | undefined;
  currencyFormat: CurrencyFormat;
  /// Built once by the list, so the memoized row keeps its identity.
  accountNumberIndex: AccountNumberIndex;
  /// All stable — the row subscribes to its own edit slice, so a memoized row
  /// re-renders only when its transaction or its own pending edits change.
  onCommitDescription: (tx: TransactionListDto, value: string) => void;
  onOpenPicker: (id: TransactionId, field: PickerField) => void;
  onViewDetails: (id: TransactionId) => void;
  onCreateRule: (tx: TransactionListDto) => void;
  onSplit: (tx: TransactionListDto) => void;
  onOpenSuggestions: (
    event: React.MouseEvent<HTMLElement>,
    txId: TransactionId,
  ) => void;
  onOpenAppliedRules: (
    event: React.MouseEvent<HTMLElement>,
    tx: TransactionListDto,
  ) => void;
};

export const TransactionRow = memo(
  ({
    transaction,
    session,
    matchingRules,
    currencyFormat,
    accountNumberIndex,
    onCommitDescription,
    onOpenPicker,
    onViewDetails,
    onCreateRule,
    onSplit,
    onOpenSuggestions,
    onOpenAppliedRules,
  }: TransactionRowProps) => {
    const edit = useRowEdit(session, transaction.id);
    const { values, pending } = useMemo(
      () => deriveRowView(transaction, edit),
      [transaction, edit],
    );

    // Read off the effective values so a pending payTo/amount edit is reflected.
    const counterparty = resolveCounterparty(
      values.payToAccount,
      accountNumberIndex,
    );
    const marker = counterpartyMarker(values.amount, counterparty);

    const entityCell = (field: Exclude<PickerField, "categories">) => (
      <EntityCell
        field={field}
        name={values[field]}
        pending={pending.has(field)}
        onClick={() => onOpenPicker(transaction.id, field)}
      />
    );

    return (
      <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
        <TableCell>
          <DateDisplay date={values.datePaid} />
        </TableCell>
        <TableCell>{entityCell("source")}</TableCell>
        <TableCell>
          {/* A grid, not a flex row: the table sizes this column from the
              cell's minimum width, and a flex row's minimum counts the full
              marker text, which at ten columns is what pushed the actions
              off-screen. A `minmax(3em, …)` track contributes only its floor
              to that minimum, so the column can shrink to the description's
              floor and the marker takes only what is left beside it. */}
          <Box
            sx={{
              display: "grid",
              // With a marker beside it the description keeps a floor, so
              // neither the text nor the inline editor gets squeezed into a
              // sliver; the marker's track grows to its text when there is
              // room and gives it up first when there is not, down to an
              // arrow and an ellipsis so the tooltip stays reachable.
              gridTemplateColumns: marker
                ? "minmax(12em, 1fr) minmax(3em, max-content)"
                : "1fr",
              columnGap: 1,
              alignItems: "center",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <DescriptionCell
                value={values.description || ""}
                placeholder={values.merchant}
                pending={pending.has("description")}
                onCommit={(v) => onCommitDescription(transaction, v)}
              />
            </Box>
            {/* Read-only, and outside the editable cell so it can't be
                mistaken for part of the description. */}
            {marker && (
              <Tooltip title={`${marker.arrow} ${marker.name}`}>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{
                    // An annotation must not win a space contest against the
                    // content it annotates: the marker ellipsises (the
                    // tooltip still carries the full name) rather than
                    // pushing the description onto extra lines.
                    minWidth: 0,
                    maxWidth: "14em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {marker.arrow} {marker.name}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>{entityCell("expenseType")}</TableCell>
        <TableCell>
          <AccountCell
            value={values.derivedAccount}
            pending={pending.has("derivedAccount")}
          />
        </TableCell>
        <TableCell>{entityCell("group")}</TableCell>
        <TableCell>
          <CategoriesCell
            names={values.categories || []}
            pending={pending.has("categories")}
            onClick={() => onOpenPicker(transaction.id, "categories")}
          />
        </TableCell>
        <TableCell align="right">
          <AmountCell
            money={values.foreignAmount}
            currencyFormat={currencyFormat}
            pending={pending.has("foreignAmount")}
          />
        </TableCell>
        <TableCell align="right">
          <AmountCell
            money={values.amount}
            currencyFormat={currencyFormat}
            pending={pending.has("amount")}
          />
        </TableCell>
        <TableCell align="center">
          <RowActionButtons
            transaction={transaction}
            matchingRules={matchingRules}
            onCreateRule={onCreateRule}
            onOpenSuggestions={onOpenSuggestions}
            onOpenAppliedRules={onOpenAppliedRules}
            onViewDetails={onViewDetails}
            onSplit={onSplit}
          />
        </TableCell>
      </TableRow>
    );
  },
);

TransactionRow.displayName = "TransactionRow";
